import { useMemo, useLayoutEffect } from "react";
import { isPageAllowedForLocale } from "@utils/localeRestrictions";
import { ResultOf, readFragment } from "gql.tada";
import { GetEventPageBySlug } from "@queries/GetEventPageBySlug";
import { MetadataHelmet } from "./MetadataHelmet";
import { BackgroundBlob } from "./BackgroundBlob";
import { useScrollReset } from "@hooks/useScrollReset";
import { Hero } from "@components/entries/Hero";
// import { ScrollCueButton } from "@components/shared/ScrollCueButton";
import { DescriptionFillSection } from "@components/entries/DescriptionFillSection";
import { DescriptionFillSectionWithCta } from "@components/entries/DescriptionFillSection/DescriptionFillSectionWithCta";
import { QuoteSlide } from "@components/entries/QuotationCarouselSection";
import { BackgroundDeepContainer } from "./BackgroundDeepContainer";
import { RoutingSection } from "@components/entries/RoutingSection";
import { VerticalRotaryCarousel } from "@components/shared/RotaryCarousel/VerticalRotaryCarousel";
import { useTheme } from "@hooks/useTheme";
import { ThemeFragment } from "@queries/fragments/ThemeFragment";
import { defaultTheme } from "@utils/ThemeContext";
import { isValidTheme } from "../../types/ThemeModel";
import SimpleCarousel from "@components/shared/SimpleCarousel";
import { AccessibleMedia } from "@components/entries/AccessibleMedia";
import { EventChapterItem } from "@components/entries/EventChapterItem";
import { NotFound } from "@components/shared/NotFound";
import { useGlobalContext } from "@utils/GlobalContext";
import { normalizeVariant } from "@utils/textUtils";

type Page = NonNullable<
  NonNullable<ResultOf<typeof GetEventPageBySlug>["eventsEventPageCollection"]>["items"][number]
>;

export const EventPage = ({
  page,
}: {
  page: Page;
}) => {
  useScrollReset();
  const { locale } = useGlobalContext();
  const { setTheme } = useTheme();
  let customTheme: boolean = false;

  const theme = useMemo(
    () => readFragment(ThemeFragment, page.theme) ?? defaultTheme,
    [page.theme]
  );

  useLayoutEffect(() => {
    if (customTheme && theme) {
      theme.palette = 'red-mx' as string;
    }

    if (!isValidTheme(theme)) {
      console.warn(`Palette combination invalid: `, theme);
      return;
    }

    setTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, customTheme]);

  if (!isPageAllowedForLocale(locale, page.showPageInLocales as string[])) {
    return <NotFound />;
  }

  // special for mx event page
  if (normalizeVariant(page?.hero?.variant) === 'image-auto-height') {
    if (page.ctaFooter) {
      page.ctaFooter.variant = 'image-auto-height';
      customTheme = true;
    }
  }

  // console.log(page);

  return (
    <div>
      <MetadataHelmet pageInfo={{ slug: page.slug, metadata: page.metadata }} />
      <BackgroundBlob />

      {page.hero &&
        <>
          <Hero entry={{ hero: page.hero }} />
          {/*<ScrollCueButton />*/}
        </>
      }

      {page.eventDescription &&
        <>
          {!customTheme &&
            <DescriptionFillSection
              entry={{ descriptionFillSection: page?.eventDescription } as any}
            />
          }
          {customTheme &&
            <DescriptionFillSectionWithCta
              entry={{ descriptionFillSection: page?.eventDescription}}
              cta={page?.eventDescriptionCta}
            />
          }
        </>
      }

      {page.imageCarousel1Collection && page.imageCarousel1Collection.items?.length > 0 && (
        <div className="container-base">
          <SimpleCarousel
            slides={page.imageCarousel1Collection.items.map((item: any) => {
              return <AccessibleMedia item={item} />
            })}
          />
        </div>
      )}

      {page.eventChaptersCollection && page.eventChaptersCollection.items?.length > 0 && (
        <div className="container-base max-w-[1440px] pb-[120px] mx-auto">
          {page.eventChaptersCollection?.items?.map((item: any) => {
            return (
              <EventChapterItem key={item?.sys?.id} item={item} customTheme={customTheme} />
            )
          })}
        </div>
      )}

      {page.testimonial && (
        <div className="py-0 dsk:py-[150px]">
          <QuoteSlide
            quote={page.testimonial.quotation ?? ""}
            name={page.testimonial.attributionName}
            position={page.testimonial.attributionPosition}
            company={page.testimonial.attributionCompany}
          />
        </div>
      )}

      {page.imageCarousel2Collection && page.imageCarousel2Collection.items?.length > 0 && (
        <div className="container-base pt-20 dsk:pt-16 pb-4">
          <SimpleCarousel
            slides={page.imageCarousel2Collection.items.map((item: any) => {
              return <AccessibleMedia item={item} />
            })}
          />
        </div>
      )}

      {page.socialArcCarouselCollection && page.socialArcCarouselCollection.items?.length > 0 && (
        <VerticalRotaryCarousel
          title={page.socialArcCarouselCollection?.items[0]?.title as any}
          entry={
            {
              rotaryCarouselSection: page.socialArcCarouselCollection?.items[0]?.itemsCollection
            } as any
          }
        />
      )}

      {page.ctaFooter && (
        <BackgroundDeepContainer>
          <RoutingSection entry={{ routingSection: page?.ctaFooter } as any} />
        </BackgroundDeepContainer>
      )}
    </div>
  );
};
